import { useState, useEffect, useRef, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CreateTaskForm } from "@/components/tasks/create-task-form";
import { getBoardEntityLabel } from "@/lib/board-types";
import type { CreateTaskFormProps } from "@/components/tasks/create-task-form";

interface CreateTaskSheetProps extends Omit<CreateTaskFormProps, "onCancel" | "onClose" | "active" | "onRequestCloseRef"> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTaskSheet({ open, onOpenChange, ...formProps }: CreateTaskSheetProps) {
  const [formKey, setFormKey] = useState(0);
  const requestCloseRef = useRef<((action: "cancel" | "close") => void) | null>(null);

  useEffect(() => {
    if (open) {
      setFormKey(k => k + 1);
    }
  }, [open]);

  const handleSheetOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && requestCloseRef.current) {
      requestCloseRef.current("close");
    } else {
      onOpenChange(nextOpen);
    }
  }, [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="task-create-sheet">
        <SheetHeader>
          <SheetTitle>New {getBoardEntityLabel(formProps.boardType)}</SheetTitle>
        </SheetHeader>

        <div className="mt-4">
          <CreateTaskForm
            key={formKey}
            {...formProps}
            active={open}
            onCancel={() => onOpenChange(false)}
            onClose={() => onOpenChange(false)}
            onRequestCloseRef={requestCloseRef}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
